/**
 * 017: structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/logistics/shipment/list/filters.tsx — its
 * `FilterElements`: one `StatusField` per filter, inline on a wide toolbar with the label hidden,
 * full-width and labelled inside the drawer.
 *
 * Only the data and labels are ours. Brand and category are route params (`?brandId=`,
 * `?categoryId=`) `GET /catalog/products` filters on server-side, written through `hrefWith` so
 * `?pageSize=` survives. Status (`?activeOnly=1`) is the status dropdown and the search box is the
 * toolbar's own, both in List.tsx; `PagedTable`'s toolbar lays these two fields out and owns Clear.
 */
import { useNavigate, useSearchParams } from "react-router-dom";
import { StatusField } from "@steelyard/theme";
import { FILTER_LABEL } from "../../../components/table/toolbar";
import { hrefWith } from "../../../lib/paging";
import type { Brand, Category } from "../../../lib/services/catalog";

export type ProductFilterValues = { brandId?: string; categoryId?: string };

export function ProductFilters({ value, brands, categories }: { value: ProductFilterValues; brands: Brand[]; categories: Category[] }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { brandId, categoryId } = value;

  // Every control resets the page — a filter change means a different result set.
  const apply = (over: Record<string, string | undefined>) =>
    navigate(hrefWith("/catalogue/products", searchParams, { ...over, page: undefined }));

  const brandOptions = brands.map((b) => ({ value: b.id, label: b.name }));
  const categoryOptions = categories.map((c) => ({ value: c.id, label: c.name }));

  return (
    <>
      <StatusField
        label="Brand"
        labelClassName={FILTER_LABEL}
        placeholder="Any brand"
        options={brandOptions}
        value={brandId ?? null}
        onChange={(next: string) => apply({ brandId: next || undefined })}
        clearable
        searchable
        onClear={() => apply({ brandId: undefined })}
        getOptionValue={(option) => option.value}
        displayValue={(selected: string) => brandOptions.find((o) => o.value === selected)?.label ?? ""}
        dropdownClassName="!z-10 h-auto"
        className="w-full @5xl:w-52"
      />
      <StatusField
        label="Category"
        labelClassName={FILTER_LABEL}
        placeholder="Any category"
        options={categoryOptions}
        value={categoryId ?? null}
        onChange={(next: string) => apply({ categoryId: next || undefined })}
        clearable
        searchable
        onClear={() => apply({ categoryId: undefined })}
        getOptionValue={(option) => option.value}
        displayValue={(selected: string) => categoryOptions.find((o) => o.value === selected)?.label ?? ""}
        dropdownClassName="!z-10 h-auto"
        className="w-full @5xl:w-52"
      />
    </>
  );
}
